import * as React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Grid, Typography, Divider } from '@mui/material';

import BookmarkBorder from '@mui/icons-material/BookmarkBorder';
import TaskAlt from '@mui/icons-material/TaskAlt';

import Countdown, { zeroPad } from 'react-countdown';

import { DateTime } from 'luxon';

import IconText from './IconText';

export default function BookableDetailsView({
  start,
  end,
  productName,
  resourceName,
  disableReservation = false,
  delayReservation = false,
}) {
  const { t } = useTranslation();
  const reservation = React.useMemo(() => {
    return Date.now() + 180000;
  }, []);

  return (
    <Grid
      id="bookables-details-view"
      container
      direction="column"
      sx={{
        // Full width of the surrounding form column so the hold divider can span
        // the same width as the content below it. The facts themselves stay a
        // compact, left-aligned 360px block centered above that wider rule.
        width: '100%',
        alignItems: 'center',
        mt: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          textAlign: 'left',
        }}
      >
        {/* Time and date are two unbreakable spans joined by a normal space, so
            on narrow screens the whole date drops to the next line intact
            rather than breaking mid-string. Wrapped in one block so they flow
            inline instead of stacking as flex rows. */}
        <Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              display: 'inline',
              whiteSpace: 'nowrap',
            }}
          >
            {`${start?.toLocaleString(
              DateTime.TIME_SIMPLE,
            )} - ${end?.toLocaleString(DateTime.TIME_SIMPLE)}`}
          </Typography>{' '}
          <Typography
            variant="body1"
            sx={{
              display: 'inline',
              whiteSpace: 'nowrap',
            }}
          >
            {start?.toLocaleString(DateTime.DATE_HUGE)}
          </Typography>
        </Box>
        {resourceName && (
          <Box sx={{ maxWidth: '100%' }}>
            <IconText icon={BookmarkBorder} sx={{ overflowWrap: 'anywhere' }}>
              {resourceName}
            </IconText>
          </Box>
        )}
        {productName && (
          <Box sx={{ maxWidth: '100%' }}>
            <IconText icon={TaskAlt} sx={{ overflowWrap: 'anywhere' }}>
              {productName}
            </IconText>
          </Box>
        )}
      </Box>
      {/* The tear-line only separates the fixed facts from the live hold, so it
          renders only while a hold is actually shown. flexItem stretches it to
          the full form-column width, wider than the 360px facts block above. */}
      {!disableReservation && (
        <Divider flexItem sx={{ mt: 0.5 }}>
          {delayReservation ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'text.secondary',
              }}
            >
              <CircularProgress color="inherit" size={12} />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {t('until_reservation_expiration', {
                  expiration: ' ',
                })}
              </Typography>
            </Box>
          ) : (
            <Countdown
              date={reservation}
              daysInHours
              renderer={({ minutes, seconds, completed }) => (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {completed
                    ? t('reservation_expired')
                    : t('until_reservation_expiration', {
                        expiration: `${zeroPad(minutes)}:${zeroPad(seconds)}`,
                      })}
                </Typography>
              )}
            />
          )}
        </Divider>
      )}
    </Grid>
  );
}

BookableDetailsView.propTypes = {
  start: PropTypes.object,
  end: PropTypes.object,
  product: PropTypes.object,
  productName: PropTypes.string,
  productMinDuration: PropTypes.number,
  resourceName: PropTypes.string,
  disableReservation: PropTypes.bool,
  delayReservation: PropTypes.bool,
};
